import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import date from "date-and-time";

import Card from "../../components/shared/Card/Card";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useTicket from "../../data/Ticket/useTicket";
import useCustomers from "../../data/Customer/useCustomers";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import useTicketMutations from "../../data/Ticket/useTicketMutations";
import useApiHelper from "../../hooks/useApiHelper";
import { errorResolver } from "../../utils/errorResolver";
import useStaffUsers from "../../data/StaffUser/useStaffUsers";
import useAuth from "../../hooks/useAuth";
import {
  ITicketAttachment,
  TicketFeatureDecision,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "../../models/Tickets/Ticket";
import useTicketAttachments from "../../data/Ticket/useTicketAttachments";
import {
  isImageAttachment,
  formatAttachmentSize,
  sortAttachmentsNewestFirst,
} from "../../utils/ticketAttachments";
import TicketChatThread from "./TicketChatThread";
import TicketImagePreviewModal from "./TicketImagePreviewModal";
import {
  TicketFeatureDecisionPill,
  TicketTypePill,
  ticketFeatureDecisionLabels,
  ticketFeatureDecisionOptions,
  ticketTypeLabels,
  ticketTypeOptions,
} from "./ticketMeta";

const ticketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Open]: "Open",
  [TicketStatus.InProgress]: "In Progress",
  [TicketStatus.Resolved]: "Resolved",
  [TicketStatus.Closed]: "Closed",
};

const ticketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.Low]: "Low",
  [TicketPriority.Medium]: "Medium",
  [TicketPriority.High]: "High",
  [TicketPriority.Urgent]: "Urgent",
};

export default function ViewTicketPage() {
  const { ticketId } = useParams();
  const { ticket } = useTicket(ticketId ?? "");
  const { customers } = useCustomers();
  const { staffUsers } = useStaffUsers();
  const { customerUsers } = useCustomerUsers(ticket.data?.customerId ?? "");
  const { update, addNote, assignToMe } = useTicketMutations();
  const { user } = useAuth();
  const { postDocs } = useApiHelper();
  const { addNotification } = useNotification();
  const { setInfo } = usePageNameContext();

  const [status, setStatus] = useState<TicketStatus>(TicketStatus.Open);
  const [ticketType, setTicketType] = useState<TicketType>(TicketType.Bug);
  const [featureDecision, setFeatureDecision] = useState<TicketFeatureDecision>(
    TicketFeatureDecision.Undecided,
  );
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [conversationReply, setConversationReply] = useState("");
  const [internalNoteText, setInternalNoteText] = useState("");
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [activeImagePreview, setActiveImagePreview] =
    useState<ITicketAttachment | null>(null);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;

  const { previewUrls, downloadAttachment } = useTicketAttachments(
    ticket.data?.id,
    ticket.data?.attachments,
  );

  useEffect(() => {
    if (!ticket.isLoading && ticket.data) {
      setInfo({
        name: `Ticket: ${ticket.data.subject}`,
        desc: "View ticket details, notes and status.",
        pageTitle: `Ticket | ${ticket.data.subject}`,
      });
    } else {
      setInfo({
        name: "Ticket",
        desc: "View ticket details, notes and status.",
        pageTitle: "Ticket",
      });
    }
  }, [ticket.isLoading, ticket.data, setInfo]);

  useEffect(() => {
    if (ticket.data) {
      setStatus(ticket.data.status);
      setTicketType(ticket.data.type ?? TicketType.Bug);
      setFeatureDecision(
        ticket.data.featureDecision ?? TicketFeatureDecision.Undecided,
      );
      setAssigneeEmail(ticket.data.assignedToUserId?.trim() ?? "");
    }
  }, [ticket.data]);

  const customerName = useMemo(() => {
    if (!ticket.data || !customers.data) return "";
    return (
      customers.data.find((c) => c.id === ticket.data?.customerId)?.name ??
      ticket.data.customerId
    );
  }, [ticket.data, customers.data]);

  const createdByLabel = useMemo(() => {
    if (!ticket.data) return "";
    const u = customerUsers.data?.find((x) => x.id === ticket.data.createdByUserId);
    if (u) {
      const name = `${u.forename} ${u.surname}`.trim();
      return name || u.username;
    }
    return ticket.data.createdByUserId;
  }, [ticket.data, customerUsers.data]);

  const internalNotes = useMemo(() => {
    if (!ticket.data?.notes?.length) return [];
    return [...ticket.data.notes]
      .filter((n) => n.isInternal)
      .sort(
        (a, b) =>
          new Date(b.created).getTime() - new Date(a.created).getTime(),
      );
  }, [ticket.data?.notes]);

  if (!ticketId) {
    return <Navigate to="/tickets" />;
  }

  if (
    ticket.isLoading ||
    customers.isLoading ||
    (ticket.data?.customerId && customerUsers.isLoading)
  ) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (ticket.isError) {
    return (
      <Alert
        severity="error"
        title="Error loading ticket"
        message={errorResolver(ticket.error)}
      />
    );
  }

  if (customers.isError) {
    return (
      <Alert
        severity="error"
        title="Error loading customers"
        message={errorResolver(customers.error)}
      />
    );
  }

  if (ticket.data?.customerId && customerUsers.isError) {
    return (
      <Alert
        severity="error"
        title="Error loading users"
        message={errorResolver(customerUsers.error)}
      />
    );
  }

  if (!ticket.data) {
    return (
      <Alert severity="warn" title="Ticket not found" message="This ticket no longer exists." />
    );
  }

  // Keeps an assignee who isn't in the Firebase directory (removed account, legacy id)
  // selectable, so saving the ticket can't silently drop them.
  const currentAssignee = ticket.data.assignedToUserId?.trim() ?? "";
  const isKnownStaff = (staffUsers.data ?? []).some(
    (x) => x.email?.toLowerCase() === currentAssignee.toLowerCase(),
  );
  const legacyAssigneeId = currentAssignee && !isKnownStaff ? currentAssignee : "";

  const isAssignedToMe =
    !!user?.email &&
    currentAssignee.toLowerCase() === user.email.toLowerCase();

  const assigneeLabel = (email?: string) => {
    if (!email?.trim()) return "Unassigned";
    const staff = (staffUsers.data ?? []).find(
      (x) => x.email?.toLowerCase() === email.trim().toLowerCase(),
    );
    return staff?.displayName || email.trim();
  };

  const handleUpdateStatus = (newStatus: TicketStatus = status) => {
    update.mutate({
      id: ticket.data!.id,
      subject: ticket.data!.subject,
      description: ticket.data!.description,
      priority: ticket.data!.priority,
      type: ticketType,
      // Only sent for features - a ticket flipped back to Bug keeps whatever decision
      // it had rather than having it reset behind the scenes.
      featureDecision:
        ticketType === TicketType.Feature ? featureDecision : undefined,
      assignedToUserId: assigneeEmail.trim() || undefined,
      status: newStatus,
    });
  };

  const handleAddPublicReply = () => {
    if (!conversationReply.trim()) return;
    addNote.mutate(
      {
        ticketId: ticket.data!.id,
        note: {
          note: conversationReply.trim(),
          isInternal: false,
        },
      },
      {
        onSuccess: () => setConversationReply(""),
      },
    );
  };

  const handleAddInternalNote = () => {
    if (!internalNoteText.trim()) return;
    addNote.mutate(
      {
        ticketId: ticket.data!.id,
        note: {
          note: internalNoteText.trim(),
          isInternal: true,
        },
      },
      {
        onSuccess: () => setInternalNoteText(""),
      },
    );
  };

  const handleUploadAttachments = async () => {
    if (!ticket.data || filesToUpload.length === 0) return;

    const oversizedFile = filesToUpload.find((file) => file.size > maxAttachmentSizeBytes);
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: `${oversizedFile.name} exceeds the 100MB limit`,
      });
      return;
    }

    try {
      setIsUploadingAttachments(true);
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file);
        await postDocs(`/ticket/${ticket.data.id}/attachments`, formData);
      }

      setFilesToUpload([]);
      ticket.refetch();
      addNotification({
        variant: "success",
        primaryText: "Attachment(s) uploaded",
      });
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to upload attachment(s)",
      });
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Link className="text-sm text-orange-700 hover:text-orange-900" to="/tickets">
          {"<- Back to tickets"}
        </Link>
      </div>

      <Card title="Ticket Details" bodyClassName="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Customer</p>
            <p className="font-medium">{customerName}</p>
          </div>
          <div>
            <p className="text-gray-500">Created by</p>
            <p className="font-medium">{createdByLabel}</p>
            {ticket.data.createdByStaffEmail ? (
              <p className="mt-1 text-xs text-slate-500 italic">
                Logged on their behalf by{" "}
                <span className="font-medium not-italic text-slate-700">
                  {ticket.data.createdByStaffName?.trim() || ticket.data.createdByStaffEmail}
                </span>
                {ticket.data.createdByStaffName?.trim() ? (
                  <span className="text-slate-400">
                    {" "}
                    ({ticket.data.createdByStaffEmail})
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          <div>
            <p className="text-gray-500">Priority</p>
            <p className="font-medium">{ticketPriorityLabels[ticket.data.priority]}</p>
          </div>
          <div>
            <p className="text-gray-500">Type</p>
            <div className="flex flex-wrap items-center gap-2">
              <TicketTypePill type={ticket.data.type ?? TicketType.Bug} />
              <TicketFeatureDecisionPill
                type={ticket.data.type ?? TicketType.Bug}
                decision={ticket.data.featureDecision}
              />
            </div>
          </div>
          <div>
            <p className="text-gray-500">Created</p>
            <p className="font-medium">
              {date.format(new Date(ticket.data.created), "DD/MM/YYYY HH:mm")}
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch">
        <div className="flex-1 min-w-0 flex flex-col gap-0">
          <Card title="Conversation" bodyClassName="p-4 flex flex-col min-h-[22rem] lg:min-h-[32rem] max-h-[calc(100vh-10rem)]">
            <TicketChatThread
              notes={ticket.data.notes ?? []}
              opening={{
                author: createdByLabel,
                subject: ticket.data.subject,
                description: ticket.data.description,
                created: ticket.data.created,
              }}
              attachments={ticket.data.attachments}
              attachmentPreviewUrls={previewUrls}
              onAttachmentClick={setActiveImagePreview}
              onAttachmentDownload={downloadAttachment}
              className="flex-1 min-h-[12rem] max-h-[50vh] lg:max-h-[min(50vh,28rem)]"
              emptyMessage="No public replies yet. Send a reply below."
            />
            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700">
                Reply to customer
              </label>
              <textarea
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm min-h-[4.5rem] resize-y bg-white"
                placeholder="Type a message the customer can see…"
                value={conversationReply}
                onChange={(e) => setConversationReply(e.target.value)}
              />
              <Button
                onClick={handleAddPublicReply}
                disabled={addNote.isPending || !conversationReply.trim()}
              >
                {addNote.isPending ? "Sending…" : "Send reply"}
              </Button>
            </div>
          </Card>
        </div>

        <div className="w-full lg:w-[22rem] xl:w-[24rem] shrink-0 flex flex-col gap-4">
          <Card title="Status & assignment" bodyClassName="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={status}
                onChange={(e) => setStatus(Number(e.target.value) as TicketStatus)}
              >
                <option value={TicketStatus.Open}>{ticketStatusLabels[TicketStatus.Open]}</option>
                <option value={TicketStatus.InProgress}>
                  {ticketStatusLabels[TicketStatus.InProgress]}
                </option>
                <option value={TicketStatus.Resolved}>
                  {ticketStatusLabels[TicketStatus.Resolved]}
                </option>
                <option value={TicketStatus.Closed}>{ticketStatusLabels[TicketStatus.Closed]}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={ticketType}
                onChange={(e) => setTicketType(Number(e.target.value) as TicketType)}
              >
                {ticketTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {ticketTypeLabels[option]}
                  </option>
                ))}
              </select>
            </div>
            {/* Internal triage state. Only meaningful for feature requests, and never
                shown to the customer - the API strips it from Betty's responses. */}
            {ticketType === TicketType.Feature ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Feature decision
                </label>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={featureDecision}
                  onChange={(e) =>
                    setFeatureDecision(
                      Number(e.target.value) as TicketFeatureDecision,
                    )
                  }
                >
                  {ticketFeatureDecisionOptions.map((option) => (
                    <option key={option} value={option}>
                      {ticketFeatureDecisionLabels[option]}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Internal only - the customer never sees this.
                </p>
              </div>
            ) : null}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign to
              </label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={assigneeEmail}
                onChange={(e) => setAssigneeEmail(e.target.value)}
              >
                <option value="">Unassigned</option>
                {legacyAssigneeId ? (
                  <option value={legacyAssigneeId}>
                    {legacyAssigneeId} (not in staff directory)
                  </option>
                ) : null}
                {(staffUsers.data ?? []).map((staff) => (
                  <option key={staff.userId} value={staff.email}>
                    {staff.displayName || staff.email}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Current: {assigneeLabel(ticket.data.assignedToUserId)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => handleUpdateStatus()} disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              {/* Assigns immediately - the API reads the email off the Firebase token,
                  so this doesn't depend on the dropdown or touch any other field. */}
              <Button
                variant="outlined"
                onClick={() => assignToMe.mutate(ticket.data!.id)}
                disabled={assignToMe.isPending || isAssignedToMe}
              >
                {isAssignedToMe
                  ? "Assigned to you"
                  : assignToMe.isPending
                    ? "Assigning…"
                    : "Assign to me"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setStatus(TicketStatus.Closed);
                  handleUpdateStatus(TicketStatus.Closed);
                }}
                disabled={update.isPending || ticket.data.status === TicketStatus.Closed}
              >
                Close ticket
              </Button>
            </div>
          </Card>

          <Card title="Internal notes" bodyClassName="p-4 space-y-3">
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-24"
              placeholder="Visible only to staff…"
              value={internalNoteText}
              onChange={(e) => setInternalNoteText(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={handleAddInternalNote}
              disabled={addNote.isPending || !internalNoteText.trim()}
            >
              {addNote.isPending ? "Saving…" : "Add internal note"}
            </Button>
            {internalNotes.length > 0 ? (
              <div className="border-t border-gray-200 pt-3 space-y-2 max-h-48 overflow-y-auto">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  History
                </p>
                {internalNotes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-lg bg-purple-50/80 border border-purple-100 px-2.5 py-2 text-xs"
                  >
                    <p className="text-[10px] text-purple-800/80 mb-1">
                      {note.authorName ?? note.authorEmail ?? "Staff"} ·{" "}
                      {date.format(new Date(note.created), "DD/MM/YYYY HH:mm")}
                    </p>
                    <p className="text-slate-800 whitespace-pre-wrap">{note.note}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>

          <Card title="Attachments" bodyClassName="p-4 space-y-3">
            {ticket.data.attachments?.length ? (
              <div className="space-y-3 max-h-56 overflow-y-auto">
                {sortAttachmentsNewestFirst(ticket.data.attachments).map(
                  (attachment) => (
                    <div key={attachment.id} className="rounded border border-gray-200 p-2">
                      {isImageAttachment(attachment.contentType) ? (
                        <button
                          type="button"
                          className="block rounded border border-gray-200 p-1 hover:border-orange-300"
                          disabled={!previewUrls[attachment.id]}
                          onClick={() => setActiveImagePreview(attachment)}
                        >
                          {previewUrls[attachment.id] ? (
                            <img
                              src={previewUrls[attachment.id]}
                              alt={attachment.fileName}
                              className="h-16 w-16 rounded object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-500">
                              …
                            </div>
                          )}
                        </button>
                      ) : null}

                      <p className="mt-1 text-xs font-medium text-slate-800 truncate">
                        {attachment.fileName}
                      </p>
                      <button
                        type="button"
                        onClick={() => downloadAttachment(attachment)}
                        className="text-[11px] font-medium text-orange-700 hover:text-orange-900"
                      >
                        Download
                      </button>
                      <p className="text-[10px] text-gray-500">
                        {formatAttachmentSize(attachment.fileSize)} ·{" "}
                        {date.format(new Date(attachment.created), "DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No attachments yet.</p>
            )}

            <div className="space-y-2 border-t border-gray-200 pt-3">
              <label className="block text-sm font-medium text-gray-700">Upload</label>
              <input
                type="file"
                multiple
                className="w-full text-xs text-gray-700"
                onChange={(e) => setFilesToUpload(Array.from(e.target.files ?? []))}
              />
              <p className="text-[11px] text-gray-500">Max 100MB per file.</p>
              {filesToUpload.length > 0 ? (
                <ul className="space-y-0.5 text-[11px] text-gray-600">
                  {filesToUpload.map((file) => (
                    <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>
                  ))}
                </ul>
              ) : null}
              <Button
                onClick={handleUploadAttachments}
                disabled={isUploadingAttachments || filesToUpload.length === 0}
              >
                {isUploadingAttachments ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <TicketImagePreviewModal
        attachment={activeImagePreview}
        src={activeImagePreview ? previewUrls[activeImagePreview.id] : undefined}
        onClose={() => setActiveImagePreview(null)}
        onDownload={downloadAttachment}
      />
    </div>
  );
}
